import React from 'react';
import type { PlantafelViewMode, UtilizationDay } from './plantafelTypes';
import { RESOURCE_COL_W } from './plantafelTypes';
import { PLANTAFEL_WEEKDAY_LABELS_SHORT } from '../../utils/plantafelSettings';
import { addDays, isoWeekNumber, localDateKey, type MonthViewportMeta } from './plantafelViewport';

function UtilizationCell({ u, label, isWeekend }: { u: UtilizationDay; label: string; isWeekend: boolean }) {
  const pct = Math.round(u.pct * 100);
  const colorBg =
    pct > 110 ? '#ef4444' : pct > 95 ? '#f59e0b' : pct > 50 ? '#22c55e' : pct > 20 ? '#86efac' : '#e5e7eb';
  return (
    <div className="flex flex-col gap-1 border-l border-app-border px-1.5 first:border-l-0">
      <div className="flex items-baseline justify-between">
        {/* Gleiche Auszeichnung wie die Wochentage im Kopf, damit Fuß und Kopf eine Zeile bilden. */}
        <span
          className={`text-xs font-semibold tracking-wide ${isWeekend ? 'text-app-text-subtle/60' : 'text-app-text-subtle'}`}
        >
          {label}
        </span>
        <span
          className={`text-xs font-semibold tabular-nums ${isWeekend ? 'text-app-text-subtle/60' : pct > 100 ? 'text-red-600' : 'text-app-text'}`}
        >
          {isWeekend ? '–' : `${pct}%`}
        </span>
      </div>
      <div className="relative h-1.5 overflow-hidden rounded bg-app-surface-muted">
        {!isWeekend && (
          <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${Math.min(pct, 100)}%`, background: colorBg }} />
        )}
      </div>
      <span className="text-xs text-app-text-subtle">
        {isWeekend ? 'Wochenende' : `${u.scheduled_hours} / ${u.capacity_hours} h`}
      </span>
    </div>
  );
}

export function UtilizationFooter({
  days,
  viewMode,
  viewportStart,
  monthMeta,
  visibleWeekdays,
}: {
  days: UtilizationDay[];
  viewMode: PlantafelViewMode;
  viewportStart: Date;
  monthMeta?: MonthViewportMeta | null;
  visibleWeekdays: number[];
}) {
  if (viewMode === 'tag') {
    const u = days[0];
    if (!u) return null;
    const label = new Intl.DateTimeFormat('de-DE', { weekday: 'short' }).format(viewportStart);
    const isWeekend = viewportStart.getDay() === 0 || viewportStart.getDay() === 6;
    return (
      <div
        className="grid shrink-0 items-center border-t border-app-border bg-app-surface px-4 py-2"
        style={{ gridTemplateColumns: `${RESOURCE_COL_W}px 1fr` }}
      >
        <div>
          <div className="text-sm font-semibold text-app-text-muted">Auslastung</div>
          <div className="text-xs text-app-text-subtle">Soll pro Werktag</div>
        </div>
        <div className="max-w-xs">
          <UtilizationCell u={u} label={label} isWeekend={isWeekend} />
        </div>
      </div>
    );
  }

  if (viewMode === 'monat' && monthMeta) {
    const weekBuckets: UtilizationDay[][] = Array.from({ length: monthMeta.weekCount }, () => []);
    for (let i = 0; i < days.length && i < monthMeta.dayCount; i++) {
      weekBuckets[Math.floor(i / 7)].push(days[i]);
    }
    const monthLabel = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(
      new Date(monthMeta.focusYear, monthMeta.focusMonth, 1),
    );
    return (
      <div
        className="grid shrink-0 items-center border-t border-app-border bg-app-surface px-4 py-2"
        style={{ gridTemplateColumns: `${RESOURCE_COL_W}px 1fr` }}
      >
        <div>
          <div className="text-sm font-semibold text-app-text-muted">Auslastung</div>
          <div className="text-xs text-app-text-subtle">{monthLabel} · Ø pro KW</div>
        </div>
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${monthMeta.weekCount}, minmax(0, 1fr))` }}
        >
          {weekBuckets.map((bucket, wi) => {
            const work = bucket.filter((_, di) => visibleWeekdays.includes(di % 7));
            const scheduled = work.reduce((s, d) => s + d.scheduled_hours, 0);
            const capacity = work.reduce((s, d) => s + d.capacity_hours, 0);
            const pct = capacity > 0 ? Math.round((scheduled / capacity) * 100) : 0;
            const weekStart = addDays(viewportStart, wi * 7);
            return (
              <div key={wi} className="rounded border border-app-border px-2 py-1.5">
                <div className="text-xs font-medium text-app-text-subtle">KW {isoWeekNumber(weekStart)}</div>
                <div className="text-xs font-semibold tabular-nums text-app-text">{pct}%</div>
                <div className="text-xs text-app-text-subtle">
                  {Math.round(scheduled)} / {Math.round(capacity)} h
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const daysByKey = React.useMemo(() => new Map(days.map((d) => [d.day, d])), [days]);

  return (
    <div
      className="grid shrink-0 items-center border-t border-app-border bg-app-surface px-4 py-2"
      style={{ gridTemplateColumns: `${RESOURCE_COL_W}px 1fr` }}
    >
      <div>
        <div className="text-sm font-semibold text-app-text-muted">Auslastung</div>
        <div className="text-xs text-app-text-subtle">KW {isoWeekNumber(viewportStart)} · Soll pro Werktag</div>
      </div>
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${visibleWeekdays.length}, minmax(0, 1fr))` }}
      >
        {visibleWeekdays.map((dow) => {
          const d = addDays(viewportStart, dow);
          const key = localDateKey(d);
          const u = daysByKey.get(key) ?? {
            day: key,
            scheduled_hours: 0,
            capacity_hours: 0,
            pct: 0,
          };
          const wknd = d.getDay() === 0 || d.getDay() === 6;
          return (
            <UtilizationCell
              key={key}
              u={u}
              label={PLANTAFEL_WEEKDAY_LABELS_SHORT[dow]}
              isWeekend={wknd}
            />
          );
        })}
      </div>
    </div>
  );
}
